import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Snackbar from "@mui/material/Snackbar";
import {
  ClickAwayListener,
  CssBaseline,
  ThemeProvider,
  Typography,
  Box,
  Button,
} from "@mui/material";
import { createTheme } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import HelpRoundedIcon from "@mui/icons-material/HelpRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import Dialog from "../components/Dialog";
import {
  clearNetworkNotice,
  isServerOutageActive,
} from "services/networkStatus";

const StateContext = createContext();

// Stable outer theme — created ONCE at module scope so the app-wide theme
// identity never changes. The app's real theme comes from App.js's own
// <ThemeProvider theme={theme}> (built from themeSettings(mode)); this outer
// provider only exists so CssBaseline and any non-App subtree have a theme.
// ⚠️ It MUST stay referentially stable: MUI's installed ThemeProvider merges
// `{...upperTheme, ...localTheme}`, so a changing outer theme recreates the
// app-wide theme object on every change and re-runs every effect with `theme`
// in its deps (the snackbar → theme → refetch churn loop). The Snackbar and
// Dialog get the per-toast `snackTheme` from their own local ThemeProvider.
const BASE_THEME = createTheme();

// ⏱️ Snackbar auto-hide is owned by this provider, not by MUI (see the
// timer effect below) — MUI's own timer pauses on `mouseenter` / window
// blur and never resumes on touch devices.
const SNACKBAR_AUTO_HIDE_MS = 10000;

// Hover-pause is wired ONLY when the device can actually hover. On touch
// devices a tap fires an emulated `mouseenter` with no matching
// `mouseleave`, so a hover-pause would freeze the auto-hide timer forever.
// Computed once at module scope: hover capability cannot change at runtime
// in practice (resizing a desktop window does not remove it).
const CAN_HOVER =
  typeof window !== "undefined" &&
  window.matchMedia?.("(hover: hover)")?.matches === true;

export const ContextProvider = ({ children }) => {
  const [snackTheme, setSnackTheme] = useState(createTheme());
  const [iconType, setIconType] = useState("check"); // Default icon type

  const [activeMenu, setActiveMenu] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [screenSize, setScreenSize] = useState(undefined);
  const [overlayName, setOverlayName] = useState(null);
  const [overlayData, setOverlayData] = useState(undefined);
  const [color, setColor] = useState(BASE_THEME.palette.background.default);
  const [customPageTitle, setCustomPageTitle] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarAction, setSnackbarAction] = useState(null);

  const [refreshPage, setRefreshPage] = useState(false);

  // ⚡ Memoized so consumers relying on these in effect/callback deps
  // do not re-run on every provider render (e.g. resize-driven renders).
  const showSnackbar = useCallback((message, theme, iconType, color, action) => {
    // While the app-wide "Reconnecting to the server…" overlay is up, routine
    // per-request error toasts are noise — the overlay is the single source of
    // truth for the outage. (Historically each snackbar also re-created the
    // app-wide theme — see BASE_THEME above — feeding a refetch loop; that
    // source is now fixed, and this guard remains as the outage UX rule.)
    // Success/info snackbars are unaffected — the "Reconnected" confirmation
    // depends on them. The guard is a live module read, so `showSnackbar`
    // stays `useCallback([])` (no identity churn).
    if (iconType === "error" && isServerOutageActive()) return;

    setSnackbarMessage(message);
    setIconType(iconType);
    setSnackTheme(theme);
    setSnackbarOpen(true);
    setSnackbarAction(action || null);

    if (color) {
      setColor(color);
    } else {
      // Use the theme's background.alt color for proper dark/light mode support
      setColor(theme.palette.background.default);
    }
  }, []);

  const triggerPageRefresh = useCallback(
    (message, theme, action) => {
      setRefreshPage((prev) => !prev);
      if (message) {
        showSnackbar(message, theme, "success", theme.palette.background.default, action);
      }
    },
    [showSnackbar],
  );

  const handleSnackbarClose = useCallback(() => {
    setSnackbarOpen(false);
    setSnackbarAction(null);
  }, []);

  // ⏱️ Self-managed snackbar auto-hide timer.
  // MUI's built-in timer (`autoHideDuration`) pauses on `mouseenter` and on
  // window blur, and on touch devices a tap fires an emulated `mouseenter`
  // with no matching `mouseleave` — so the timer stayed paused permanently
  // and the toast never hid on mobile. `autoHideDuration={null}` disables
  // MUI's timer entirely; this ref-owned timeout is the single source of
  // truth on every device.
  const snackbarTimerRef = useRef(null);

  const clearSnackbarTimer = useCallback(() => {
    if (snackbarTimerRef.current !== null) {
      clearTimeout(snackbarTimerRef.current);
      snackbarTimerRef.current = null;
    }
  }, []);

  const startSnackbarTimer = useCallback(() => {
    clearSnackbarTimer();
    snackbarTimerRef.current = setTimeout(() => {
      snackbarTimerRef.current = null;
      handleSnackbarClose();
    }, SNACKBAR_AUTO_HIDE_MS);
  }, [clearSnackbarTimer, handleSnackbarClose]);

  // Start on open; always clear on close, replacement, or unmount.
  // ⚠️ Keyed on `snackbarOpen` only (mirrors MUI's previous behaviour): a
  // toast that replaces an already-open one does NOT restart the 10s
  // window, so a stream of repeated errors can never pin the toast open.
  useEffect(() => {
    if (!snackbarOpen) {
      clearSnackbarTimer();
      return undefined;
    }
    startSnackbarTimer();
    return clearSnackbarTimer;
  }, [snackbarOpen, startSnackbarTimer, clearSnackbarTimer]);

  const getIcon = (theme) => {
    switch (iconType) {
      case "error":
        return (
          <CancelRoundedIcon
            sx={{
              fontSize: 35,
              color: theme.palette.error.main,
              marginRight: "1rem",
            }}
          />
        );
      case "info":
        return (
          <HelpRoundedIcon
            sx={{
              fontSize: 35,
              color: theme.palette.info.main,
              marginRight: "1rem",
            }}
          />
        );
      default:
      case "success":
        return (
          <CheckRoundedIcon
            sx={{
              fontSize: 35,
              color: theme.palette.primary.main,
              marginRight: "1rem",
            }}
          />
        );
    }
  };

  // 🧩 Dialog state
  const [dialogConfig, setDialogConfig] = useState({
    open: false,
    title: "",
    message: "",
    customContent: null,
    primaryAction: null,
    secondaryAction: null,
    tertiaryAction: null,
    primaryOnClick: null,
    secondaryOnClick: null,
    tertiaryOnClick: null,
    isNetworkNotice: false,
  });
  
  // 🧼 Function to close dialog
  const closeDialog = useCallback(() => {
    // Closing the dialog also releases the network-notice lock: reset the
    // state flag so scene dialogs regain normal behavior, and clear the
    // module-level notice so EVERY dismissal path (OK, ESC, backdrop, or a
    // stale handler calling closeDialog directly) re-arms future notices.
    clearNetworkNotice();
    setDialogConfig((prev) => ({
      ...prev,
      open: false,
      primaryDisabled: false,
      isNetworkNotice: false,
    }));
  }, []);

  // 🪄 Function to open a dialog
  const openDialog = useCallback(
    (config) => {
      setDialogConfig((prev) => {
        // A network-notice dialog must not be replaced by a routine error
        // dialog (React 18 batches the interceptor's notice + the screen's
        // catch handler in the same microtask drain, and the screen's would
        // otherwise overwrite it). The guard reads `prev` INSIDE the
        // functional updater on purpose: queued updaters apply in order
        // against the latest state, so it still blocks when a screen calls a
        // stale `openDialog` reference captured before the notice opened
        // (a closure read would miss that). Scenes regain normal behavior as
        // soon as the notice is dismissed.
        if (prev.isNetworkNotice && !config.isNetworkNotice) return prev;

        // While the "Reconnecting to the server…" panel is up, routine scene
        // error dialogs ("Unable to connect…") are noise behind it — and
        // would linger after recovery. They are ignored until the outage
        // clears. The check reads the LIVE module flag (not a closure
        // snapshot), so a stale `openDialog` reference captured before the
        // outage still obeys it — same reasoning as the notice lock above.
        if (isServerOutageActive() && !config.isNetworkNotice) return;

        return {
          theme: config.theme,
          open: true,
          title: config.title || "",
          message: config.message || "",
          customContent: config.customContent || null,
          primaryAction: config.primaryAction || null,
          secondaryAction: config.secondaryAction || null,
          tertiaryAction: config.tertiaryAction || null,
          // Flag lives on the dialog state so the lock survives re-renders
          // until closeDialog releases it.
          isNetworkNotice: config.isNetworkNotice || false,
          primaryOnClick: (e) => {
            setDialogConfig((prevState) => ({
              ...prevState,
              primaryDisabled: true,
            }));
            if (config.primaryOnClick) config.primaryOnClick(e);
            else closeDialog();
          },
          secondaryOnClick: config.secondaryOnClick || closeDialog,
          tertiaryOnClick: config.tertiaryOnClick || closeDialog,
          primaryDisabled: false,
        };
      });
    },
    [closeDialog],
  );

  // ⚠️ Helper for quick error dialogs
  const handleErrorDialog = useCallback(
    (message, theme) => {
      openDialog({
        theme: theme,
        title: "Error",
        message,
        primaryAction: "OK",
        primaryOnClick: closeDialog,
      });
    },
    [openDialog, closeDialog],
  );

  const contextValue = useMemo(
    () => ({
      activeMenu,
      setActiveMenu,
      isCollapsed,
      setIsCollapsed,
      screenSize,
      setScreenSize,
      overlayName,
      setOverlayName,
      overlayData,
      setOverlayData,
      refreshPage,
      triggerPageRefresh,
      showSnackbar,
      openDialog,
      closeDialog,
      handleErrorDialog,
      customPageTitle,
      setCustomPageTitle,
    }),
    [
      activeMenu,
      setActiveMenu,
      isCollapsed,
      setIsCollapsed,
      screenSize,
      setScreenSize,
      overlayName,
      setOverlayName,
      overlayData,
      setOverlayData,
      refreshPage,
      triggerPageRefresh,
      showSnackbar,
      openDialog,
      closeDialog,
      handleErrorDialog,
      customPageTitle,
      setCustomPageTitle,
    ],
  );

  return (
    <ThemeProvider theme={BASE_THEME}>
      <CssBaseline />
      <StateContext.Provider value={contextValue}>
        {children}

        {/* Per-toast theme: Snackbar/Dialog keep the theme passed to
            showSnackbar/openDialog (dark-mode correctness), while the outer
            BASE_THEME stays referentially stable for the whole app. */}
        <ThemeProvider theme={snackTheme}>
          <Dialog
            theme={dialogConfig.theme}
            openDialog={dialogConfig.open}
            title={dialogConfig.title}
            errorMessage={dialogConfig.message}
            customContent={dialogConfig.customContent}
            primaryAction={dialogConfig.primaryAction}
            secondaryAction={dialogConfig.secondaryAction}
            tertiaryAction={dialogConfig.tertiaryAction}
            primaryOnClick={dialogConfig.primaryOnClick}
            secondaryOnClick={dialogConfig.secondaryOnClick}
            tertiaryOnClick={dialogConfig.tertiaryOnClick}
          />
        </ThemeProvider>

        <ThemeProvider theme={snackTheme}>
          {/* Click-away dismissal is guaranteed here too (MUI v6 also wraps
              its Snackbar in an internal ClickAwayListener; this outer one
              keeps the behaviour independent of slot resolution). It only
              fires for clicks OUTSIDE the toast, so the content Box still
              receives its own clicks. */}
          <ClickAwayListener onClickAway={handleSnackbarClose}>
            <Snackbar
              open={snackbarOpen}
              autoHideDuration={null}
              onClose={handleSnackbarClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
              <Box
                display="flex"
                alignItems="center"
                p="1.2rem 2.2rem"
                bgcolor={color}
                boxShadow={snackTheme.shadows[3]}
                borderRadius="20px"
                onClick={handleSnackbarClose}
                onMouseEnter={CAN_HOVER ? clearSnackbarTimer : undefined}
                onMouseLeave={CAN_HOVER ? startSnackbarTimer : undefined}
                sx={{ cursor: "pointer" }}
              >
                {getIcon(snackTheme)}
                <Typography variant="subtitle1" fontWeight="bold">
                  {snackbarMessage}
                </Typography>
                {snackbarAction && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(e) => {
                      // Let the action own the event: it closes the toast and
                      // runs its handler once, without also re-triggering the
                      // Box's dismiss-on-click above.
                      e.stopPropagation();
                      handleSnackbarClose();
                      snackbarAction.onClick();
                    }}
                    sx={{ ml: 2, whiteSpace: "nowrap", flexShrink: 0 }}
                  >
                    {snackbarAction.label}
                  </Button>
                )}
              </Box>
            </Snackbar>
          </ClickAwayListener>
        </ThemeProvider>
      </StateContext.Provider>
    </ThemeProvider>
  );
};

export const useStateContext = () => useContext(StateContext);
